/* eslint-disable react/no-unknown-property */
import {
  Action,
  ActionDropDown,
  ActionIconDropdown,
  Card,
  CardSkeleton,
  HStack,
  ICON_CHECK,
  ICON_CUSTOM_SORT_PLUS,
  ICON_HEART_OUTLINE,
  ICON_SORT,
  Message,
  Select,
  Stack,
  SimpleTable,
  SimpleTableSkeleton,
  Spacing,
} from '@doveit/bricks';
import React from 'react';
import SearchByQueryAction from '../../../components/search-by-query-action/SearchByQueryAction';
import useFilters from '../../../hooks/use-filters/useFilters';
import useIsDevice from '../../../hooks/use-is-device/useIsDevice';
import CreateIntentAction from '../../containers/create-intent-action/CreateIntentAction';
import ViewSerpIntentCard from '../../containers/view-serp-intent-card/ViewSerpIntentCard';
import ViewSerpIntentRow from '../../containers/view-serp-intent-row/ViewSerpIntentRow';
import { useSerpIntent } from '../../hooks/use-serp-intent/useSerpIntent';
import AgentFilterAction from '../../../components/agent-filter-action/AgentFilterAction';
import PaginatedList from '../../../components/paginated-list/PaginatedList';
import { useAgentFilter } from '../../../hooks/agent-filter/useAgentFilter';
import usePagination from '../../../hooks/use-pagination/usePagination';
import {
  ALL_INTENTS_FILTER_OPTION,
  DEFAULT_FILTER_OPTION, DEFAULT_SORT_OPTION, EMPTY_SERP_INTENTS, FILTER_OPTIONS, FILTERS_MAPPER, GROUPED_FILTER_OPTIONS, MOST_RECENT_CREATION_SORT_OPTION,
  SERP_INTENTS_ERROR_MESSAGE, SORT_OPTIONS, SORT_OPTIONS_PER_FILTER,
} from './constants';
import { IntentSerpPageFilterKeys } from './types';
import getShortAgentName from '../../../utils/agent/getShortAgentName';
import SerpFiltersSummary from '../../../components/serp-filters-summary/SerpFiltersSummary';
import SimplePageLayout from '../../../layouts/simple-page-layout/SimplePageLayout';

const IntentSerpPage: React.FC = () => {
  const isMobile = useIsDevice('mobile');
  const scrollRef = React.useRef<HTMLDivElement>(null);
  const { page, paginationQueryParam, goToPage } = usePagination('page', scrollRef);
  const {
    filters, addFilters, removeFilter, removeFilters,
  } = useFilters(FILTERS_MAPPER);

  const sortOption = React.useMemo(() => {
    if (filters.sort && filters.sort !== DEFAULT_SORT_OPTION.key) {
      return SORT_OPTIONS.find(({ key }) => key === filters.sort);
    }
    if (filters.filter) {
      return SORT_OPTIONS_PER_FILTER[filters.filter!];
    }
    return MOST_RECENT_CREATION_SORT_OPTION;
  }, [filters.filter, filters.sort]);

  const {
    agentEmails: agents,
    isExplicit: isAgentFilterExplicit,
    replaceFilter: replaceAgentFilter,
    isLoading: isAgentFilterLoading,
  } = useAgentFilter(paginationQueryParam, getShortAgentName);
  const agentEmail = React.useMemo(() => agents.map((it) => it.email || ''), [agents]);

  const {
    data: serpIntents,
    isLoading: isSerpIntentsLoading,
    error: serpIntentsError,
    mutate: mutateSerpIntents,
  } = useSerpIntent(isAgentFilterLoading
    ? undefined
    : {
      q: filters.q,
      agentEmail,
      ...(FILTER_OPTIONS.find(({
        key,
      }) => key === filters.filter)?.value),
    }, {
    page,
    sort: sortOption?.value,
  });

  const filterLabel = React.useMemo(() => {
    if (filters.filter === ALL_INTENTS_FILTER_OPTION.key) {
      return '';
    }

    const filterObject = FILTER_OPTIONS.find((option) => option.key === filters.filter);

    return filterObject?.label.long.toLowerCase();
  }, [filters.filter]);

  const handleSerpIntentsUpdate = React.useCallback(async () => {
    await mutateSerpIntents();
  }, [mutateSerpIntents]);

  const handleSearch = React.useCallback((query: string) => {
    if (query) {
      addFilters({ q: query }, { deleteKeys: [paginationQueryParam] });
    } else {
      removeFilters(['q', paginationQueryParam]);
    }
  }, [addFilters, paginationQueryParam, removeFilters]);

  const handleBadgeClick = React.useCallback((badgeFilter: IntentSerpPageFilterKeys) => () => {
    removeFilter(paginationQueryParam);
    addFilters({ filter: badgeFilter });
  }, [addFilters, paginationQueryParam, removeFilter]);

  return (
    <SimplePageLayout
      toolbar={(
        <HStack
          alignItems="center"
          justifyContent="space-between"
          gap={100}
        >
          <div style={{ flexGrow: '1' }}>
            {!isMobile && (
              <HStack aria-label="Filtri (desktop)" gap={150}>
                <Action
                  label={DEFAULT_FILTER_OPTION.label.short}
                  aria-label="Interessi non visti"
                  onClick={filters.filter !== DEFAULT_FILTER_OPTION.key
                    ? handleBadgeClick(DEFAULT_FILTER_OPTION.key)
                    : undefined}
                  color={filters.filter !== DEFAULT_FILTER_OPTION.key
                    ? 'neutral'
                    : 'primary'}
                />

                {GROUPED_FILTER_OPTIONS.map(({ label, options }) => (
                  <ActionDropDown
                    key={label}
                    label={label}
                    aria-label={`Filtra interessi per ${label}`}
                    emphasis="low"
                    color={options.some(({ key }) => key === filters.filter) ? 'primary' : 'neutral'}
                    options={options.map(({ key, label: optionLabel }) => ({
                      key,
                      label: optionLabel.short,
                      color: filters.filter !== key ? 'neutral' : 'primary',
                      iconRight: filters.filter !== key ? undefined : { path: ICON_CHECK },
                      onClick: filters.filter !== key ? handleBadgeClick(key) : undefined,
                    }))}
                  />
                ))}

                <Action
                  label={ALL_INTENTS_FILTER_OPTION.label.short}
                  aria-label="Tutti gli interessi"
                  onClick={filters.filter !== ALL_INTENTS_FILTER_OPTION.key
                    ? handleBadgeClick(ALL_INTENTS_FILTER_OPTION.key)
                    : undefined}
                  color={filters.filter !== ALL_INTENTS_FILTER_OPTION.key
                    ? 'neutral'
                    : 'primary'}
                />
              </HStack>
            )}
            {isMobile && (
              <Select
                value={filters.filter}
                aria-label="Seleziona un filtro dell'interesse (mobile)"
                options={FILTER_OPTIONS.map(({ label, key }) => ({
                  label: label.long,
                  value: key,
                }))}
                size="S"
                onChange={({ target }) => handleBadgeClick(target.value as IntentSerpPageFilterKeys)()}
              />
            )}
          </div>
          <HStack
            alignItems="center"
            gap={100}
          >
            <SearchByQueryAction
              initialQuery={filters.q}
              onSearch={handleSearch}
              width="20rem"
              placeholder="Cerca per contatto o immobile"
              size={!isMobile ? 'M' : 'S'}
              variant={!isMobile ? 'input' : 'action'}
            />
            <AgentFilterAction
              size={!isMobile ? 'M' : 'S'}
              onSubmit={(emails) => replaceAgentFilter(emails)()}
            />
            <ActionIconDropdown
              label="Ordina"
              aria-label="Pulsante per selezionare l'ordinamento degli interessi"
              placement="bottom-end"
              icon={{ path: !filters.sort || filters.sort !== DEFAULT_SORT_OPTION.key ? ICON_CUSTOM_SORT_PLUS : ICON_SORT }}
              size={!isMobile ? 'M' : 'S'}
              color={filters.sort && filters.sort !== DEFAULT_SORT_OPTION.key ? 'primary' : 'neutral'}
              emphasis="low"
              options={SORT_OPTIONS.map(({ key, label }) => ({
                key,
                label,
                iconRight:
                  // ICON_CHECK on "default" if no sort option has been selected
                  (!filters.sort && key === DEFAULT_SORT_OPTION.key)
                    // or the sort option is the current one
                    || filters.sort === key ? { path: ICON_CHECK } : undefined,
                color:
                  // Apply neutral color if "default" sort option has been selected
                  key !== DEFAULT_SORT_OPTION.key
                    // Apply the primary color if the sort option is the current one
                    && filters.sort === key
                    ? 'primary'
                    : 'neutral',
                onClick: () => addFilters({ sort: key }),
              }))}
            />
          </HStack>
        </HStack>
      )}
    >
      <Card
        paddingSize="M"
        aria-label="Titolo pagina"
      >
        <Card.Header
          icon={{ path: ICON_HEART_OUTLINE }}
          title={`Interessi${filterLabel ? ` ${filterLabel}` : ''}${(serpIntents && serpIntents.totalElements > 0) ? ` (${serpIntents.totalElements})` : ''}`}
          primaryActions={[
            <CreateIntentAction
              aria-label="Pulsante per aggiungere un nuovo interesse d'acquisto"
              onSuccess={handleSerpIntentsUpdate}
            >
              {({ createIntent }) => (
                <Action
                  label="Aggiungi interesse"
                  aria-label="Aggiungi un nuovo interesse d'acquisto"
                  size="S"
                  onClick={createIntent}
                />
              )}
            </CreateIntentAction>,
          ]}
        />
        <Card.Content>
          <SerpFiltersSummary
            aria-label="search-filters-summary"
            filters={{ ...filters, agentFilters: { values: agents, isExplicit: isAgentFilterExplicit } }}
            sortOptions={SORT_OPTIONS}
          />
          <Spacing
            aria-label="Lista interessi"
            margin={[300, 0, 0]}
          >
            {isSerpIntentsLoading && !isMobile && (
              <SimpleTableSkeleton aria-label="Sezione interessi in caricamento" />
            )}
            {isSerpIntentsLoading && isMobile && (
              <Stack aria-label="Sezione interessi in caricamento">
                <CardSkeleton />
                <CardSkeleton />
                <CardSkeleton />
              </Stack>
            )}
            {serpIntentsError && !isSerpIntentsLoading && (
              <Message message={SERP_INTENTS_ERROR_MESSAGE} type="critical" boxed />
            )}
            {(!isSerpIntentsLoading && (serpIntents && serpIntents.totalElements > 0)) && (
              <PaginatedList
                {...serpIntents}
                hideResultCount
                goToPage={goToPage}
              >
                {isMobile && serpIntents.content.map((serpIntent) => (
                  <ViewSerpIntentCard
                    key={serpIntent.intent.id}
                    serpIntent={serpIntent}
                    onIntentUpdate={handleSerpIntentsUpdate}
                    onAppointmentUpdate={handleSerpIntentsUpdate}
                    onOfferUpdate={handleSerpIntentsUpdate}
                    onReminderCreated={handleSerpIntentsUpdate}
                  />
                ))}
                {!isMobile && (
                  <SimpleTable>
                    <SimpleTable.Body>
                      {serpIntents.content.map((serpIntent) => (
                        <SimpleTable.Row
                          asChild
                          key={serpIntent.intent.id}
                        >
                          <ViewSerpIntentRow
                            serpIntent={serpIntent}
                            onIntentUpdate={handleSerpIntentsUpdate}
                            onAppointmentUpdate={handleSerpIntentsUpdate}
                            onOfferUpdate={handleSerpIntentsUpdate}
                            onReminderCreated={handleSerpIntentsUpdate}
                          />
                        </SimpleTable.Row>
                      ))}
                    </SimpleTable.Body>
                  </SimpleTable>
                )}
              </PaginatedList>
            )}
            {(serpIntents && serpIntents.totalElements === 0) && (
              <Message message={EMPTY_SERP_INTENTS} boxed />
            )}
          </Spacing>
        </Card.Content>
      </Card>
    </SimplePageLayout>
  );
};

export default IntentSerpPage;
